import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Clock, Crown, CreditCard, TrendingUp, BarChart3, BookOpen, Eye, DollarSign, Plus, ChevronRight, Sparkles, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getUserFavorites, getReadingHistory, getMySubscription, getPaymentHistory, getMyFeedback, getCreatorDashboard } from '../services/api';
import { ComicCard } from '../components/ComicCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CreatorEarningsDashboard } from '../components/CreatorEarningsDashboard';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

function DashboardPage() {
  const { user, isCreator, isContributor } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [history, setHistory] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [creatorData, setCreatorData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const promises = [
          getUserFavorites(),
          getReadingHistory(),
          getMySubscription(),
          getPaymentHistory()
        ];
        
        if (isContributor) promises.push(getMyFeedback());
        if (isCreator) promises.push(getCreatorDashboard());
        
        const results = await Promise.all(promises);
        setFavorites(results[0].data);
        setHistory(results[1].data);
        setSubscription(results[2].data);
        setPayments(results[3].data);
        
        if (isContributor && results[4]) setFeedback(results[4].data);
        if (isCreator) {
          const creatorIdx = isContributor ? 5 : 4;
          if (results[creatorIdx]) setCreatorData(results[creatorIdx].data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [isCreator, isContributor]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
          <p className="text-[var(--text-secondary)]">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'contributor': return { color: 'bg-purple-500/10 text-purple-500 border-purple-500/30', label: 'Contributor' };
      case 'creator': return { color: 'bg-blue-500/10 text-blue-500 border-blue-500/30', label: 'Creator' };
      default: return { color: 'bg-[var(--surface)] text-[var(--text-secondary)] border-[var(--border)]', label: 'Basic' };
    }
  };

  const tier = getTierBadge(user?.subscription_tier);

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="dashboard-page">
      <SEO title="Dashboard" />
      
      {/* Header Section */}
      <div className="bg-gradient-to-b from-[var(--surface)] to-[var(--background)] border-b border-[var(--border)]">
        <div className="page-padding py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              {/* Avatar */}
              <div className="relative">
                <div className="w-24 h-24 rounded-2xl border-2 border-[var(--border)] overflow-hidden bg-[var(--surface)]">
                  {user?.profile_picture ? (
                    <img src={user.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl font-teko text-[var(--primary)]">
                      {user?.username?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                {isCreator && (
                  <div className="absolute -bottom-1 -right-1 p-1.5 bg-[var(--primary)] rounded-lg">
                    <Sparkles size={14} className="text-white" />
                  </div>
                )}
              </div>
              
              {/* User Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="text-3xl font-teko" data-testid="user-name">{user?.username}</h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${tier.color}`}>
                    {tier.label}
                  </span>
                </div>
                <p className="text-[var(--text-secondary)]">{user?.email}</p>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3">
                <Link to="/profile" className="px-4 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg hover:border-[var(--primary)]/50 transition-colors text-sm">
                  Edit Profile
                </Link>
                {(user?.subscription_tier === 'free' || user?.subscription_tier === 'basic' || !user?.subscription_tier) && (
                  <Link to="/pricing" className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors text-sm flex items-center gap-2">
                    <Crown size={16} />
                    Upgrade
                  </Link>
                )}
              </div>
            </div>

            {/* Creator Stats */}
            {creatorData && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                  <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[var(--primary)]/10 rounded-lg">
                        <BookOpen size={20} className="text-[var(--primary)]" />
                      </div>
                      <div>
                        <p className="text-2xl font-teko">{creatorData.total_comics}</p>
                        <p className="text-xs text-[var(--text-secondary)]">Comics</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[var(--secondary)]/10 rounded-lg">
                        <Eye size={20} className="text-[var(--secondary)]" />
                      </div>
                      <div>
                        <p className="text-2xl font-teko">{(creatorData.total_views || 0).toLocaleString()}</p>
                        <p className="text-xs text-[var(--text-secondary)]">Views</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-pink-500/10 rounded-lg">
                        <Heart size={20} className="text-pink-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-teko">{(creatorData.total_likes || 0).toLocaleString()}</p>
                        <p className="text-xs text-[var(--text-secondary)]">Likes</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500/10 rounded-lg">
                        <DollarSign size={20} className="text-green-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-teko">${(creatorData.total_donations_received || 0).toFixed(2)}</p>
                        <p className="text-xs text-[var(--text-secondary)]">Earnings</p>
                      </div>
                    </div>
                  </div>
                </div>
              
                {/* Creator Quick Actions */}
                <div className="mt-6">
                  {/* Featured: Creator Studio */}
                  <Link 
                    to="/creator-studio" 
                    className="block p-6 mb-4 bg-gradient-to-r from-[#E63946]/10 via-[#FF6B35]/10 to-[#FFB703]/10 border-2 border-[#E63946]/30 rounded-xl hover:border-[#E63946]/60 transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-br from-[#E63946] to-[#FF6B35] rounded-xl">
                          <Sparkles size={28} className="text-white" />
                        </div>
                        <div>
                          <p className="font-teko text-2xl bg-gradient-to-r from-[#E63946] to-[#FF6B35] bg-clip-text text-transparent">Comic Creator Studio</p>
                          <p className="text-sm text-[var(--text-secondary)]">Upload, AI enhance & publish - all in one unified workflow!</p>
                        </div>
                      </div>
                      <ChevronRight size={24} className="text-[var(--primary)] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Link to="/create" className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)]/50 transition-all group">
                      <Plus size={24} className="text-[var(--primary)] mb-2" />
                      <p className="font-teko text-lg group-hover:text-[var(--primary)]">Quick Upload</p>
                    </Link>
                    <Link to="/ai-tools" className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-purple-500/50 transition-all group">
                      <Sparkles size={24} className="text-purple-400 mb-2" />
                      <p className="font-teko text-lg group-hover:text-purple-400">AI Tools Only</p>
                    </Link>
                    <Link to="/analytics" className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)]/50 transition-all group">
                      <BarChart3 size={24} className="text-blue-400 mb-2" />
                      <p className="font-teko text-lg group-hover:text-blue-400">Analytics</p>
                    </Link>
                    <Link to="/creator/earnings" className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)]/50 transition-all group">
                      <DollarSign size={24} className="text-green-400 mb-2" />
                      <p className="font-teko text-lg group-hover:text-green-400">Withdraw</p>
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-padding py-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="favorites" className="w-full">
            <TabsList className="w-full justify-start bg-[var(--surface)] border border-[var(--border)] rounded-xl mb-8 flex-wrap p-1">
              <TabsTrigger value="favorites" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white" data-testid="tab-favorites">
                <Heart size={16} className="mr-2" />
                Favorites
              </TabsTrigger>
              <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white" data-testid="tab-history">
                <Clock size={16} className="mr-2" />
                History
              </TabsTrigger>
              {isCreator && (
                <>
                  <TabsTrigger value="my-comics" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white" data-testid="tab-comics">
                    <BookOpen size={16} className="mr-2" />
                    My Comics
                  </TabsTrigger>
                  <TabsTrigger value="earnings" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white" data-testid="tab-earnings">
                    <TrendingUp size={16} className="mr-2" />
                    Earnings
                  </TabsTrigger>
                  <TabsTrigger value="messages" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white" data-testid="tab-messages">
                    <MessageCircle size={16} className="mr-2" />
                    Messages
                  </TabsTrigger>
                </>
              )}
              <TabsTrigger value="payments" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white" data-testid="tab-payments">
                <CreditCard size={16} className="mr-2" />
                Payments
              </TabsTrigger>
            </TabsList>

            <TabsContent value="favorites" data-testid="favorites-content">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-teko">Your Favorites</h2>
                <Link to="/browse" className="text-sm text-[var(--primary)] hover:underline flex items-center gap-1">
                  Browse more <ChevronRight size={16} />
                </Link>
              </div>
              {favorites.length === 0 ? (
                <EmptyState icon={Heart} title="No favorites yet" description="Start adding comics to your favorites to see them here" buttonText="Browse Comics" buttonLink="/browse" />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {favorites.map((comic) => <ComicCard key={comic.id} comic={comic} compact />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="history" data-testid="history-content">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-teko">Continue Reading</h2>
              </div>
              {history.length === 0 ? (
                <EmptyState icon={Clock} title="No reading history" description="Start reading comics to track your progress" buttonText="Start Reading" buttonLink="/browse" />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {history.map((comic) => <ComicCard key={comic.id} comic={comic} compact />)}
                </div>
              )}
            </TabsContent>

            {isCreator && (
              <>
                <TabsContent value="my-comics" data-testid="comics-content">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-teko">My Comics</h2>
                    <Link to="/create" className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors flex items-center gap-2">
                      <Plus size={18} />
                      Create New
                    </Link>
                  </div>
                  {!creatorData?.comics?.length ? (
                    <EmptyState icon={Crown} title="No comics published" description="Start creating and share your stories with the world" buttonText="Create Your First Comic" buttonLink="/create" />
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                      {creatorData.comics.map((comic) => <ComicCard key={comic.id} comic={comic} compact />)}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="earnings" data-testid="earnings-content">
                  <CreatorEarningsDashboard />
                </TabsContent>

                <TabsContent value="messages" data-testid="messages-content">
                  <CreatorMessagesSection />
                </TabsContent>
              </>
            )}

            <TabsContent value="payments" data-testid="payments-content">
              <h2 className="text-2xl font-teko mb-6">Payment History</h2>
              {payments.length === 0 ? (
                <EmptyState icon={CreditCard} title="No payment history" description="Your transactions will appear here" />
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div key={payment.id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium capitalize">{payment.type?.replace('_', ' ')}</p>
                        <p className="text-sm text-[var(--text-secondary)]">
                          {new Date(payment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-teko">${payment.amount?.toFixed(2)}</p>
                        <span className={`text-xs ${payment.payment_status === 'paid' ? 'text-green-500' : 'text-[var(--text-secondary)]'}`}>
                          {payment.payment_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ icon: Icon, title, description, buttonText, buttonLink }) {
  return (
    <div className="text-center py-16 bg-[var(--surface)] border border-[var(--border)] rounded-2xl">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--background)] flex items-center justify-center">
        <Icon size={28} className="text-[var(--text-secondary)]" />
      </div>
      <h3 className="text-xl font-teko mb-2">{title}</h3>
      <p className="text-[var(--text-secondary)] mb-6 max-w-sm mx-auto">{description}</p>
      {buttonText && buttonLink && (
        <Link to={buttonLink} className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors">
          {buttonText}
        </Link>
      )}
    </div>
  );
}

// Creator Messages Section
function CreatorMessagesSection() {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/creator/admin-messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/creator/admin-messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/creator/admin-messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newMessage })
      });

      if (res.ok) {
        setNewMessage('');
        fetchMessages();
        toast.success('Message sent to admin');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-teko">Messages with Admin</h2>
        {unreadCount > 0 && (
          <span className="px-3 py-1 bg-[var(--primary)] text-white text-sm rounded-full">
            {unreadCount} new
          </span>
        )}
      </div>
      
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
        {/* Messages */}
        <div className="max-h-96 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <p className="text-center text-[var(--text-secondary)] py-8">
              No messages yet. Send a message to the admin team below.
            </p>
          ) : (
            messages.map((msg, idx) => (
              <div key={msg.id || idx} className={`flex ${msg.from_admin ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[70%] p-3 rounded-xl ${
                  msg.from_admin 
                    ? 'bg-[var(--background)] border border-[var(--border)]' 
                    : 'bg-[var(--primary)] text-white'
                }`}>
                  <p className="text-sm">{msg.content}</p>
                  <p className={`text-xs mt-1 ${msg.from_admin ? 'text-[var(--text-secondary)]' : 'text-white/70'}`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Input */}
        <form onSubmit={sendMessage} className="p-4 border-t border-[var(--border)] flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message to admin..."
            className="flex-1 px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)]"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}

export default DashboardPage;
